import { useState } from "react";
import { DateTime } from "luxon";

import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { PharmacyOrdersStatusView } from "@/Common/Components/PharmacyOrders/PharmacyOrdersStatusView";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { LuxonDateHelper } from "@/Common/Helpers/LuxonDateHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PhoneNumber } from "@/Common/Models/Contacts/PhoneNumber";
import { Guid } from "@/Common/Models/Guid";
import { PharmacyOrderDistributionType, PharmacyOrdersSortOrderType, PharmacyOrderStatus, RedirectTo, SortDirection } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { Icon } from "@/DesignSystem";
import { IconBadge } from "@/DesignSystem/Molecules/IconBadge/IconBadge";
import { PharmacyOrderHelper } from "../Helper/PharmacyOrderHelper";
import { PharmacyOrderModel } from "../Models/PharmacyOrderModel";
import { PharmacyOrdersTableSettingsModel } from "../Models/PharmacyOrdersTableSettingsModel";

import styles from "@/PharmacyOrders/Components/PharmacyOrdersInbox.module.scss";

import ArrowClockwise from "@/DesignSystem/Atoms/Icons/Types/ArrowClockwise/arrow-clockwise-bold.svg";

interface IUsePharmacyOrdersTableReturn {
    columns: Readonly<Immutable.List<MyleTableColumn>>;
    tableRenderOptions: Readonly<ITableRenderOptions>;
    columnConfigurationModels: ColumnConfigurationModel[];
    saveOptionKey: string;
}

const Columns = {
    Checkbox: "Checkbox",
    Clinic: "Clinic",
    Prescriber: "Prescriber",
    PrescriptionId: "PrescriptionId",
    OrderId: "OrderId",
    LastUpdated: "LastUpdated",
    PreferredDate: "PreferredDate",
    Distribution: "Distribution",
    Prescription: "Prescription",
    OrderStatus: "OrderStatus",
    Patient: "Patient",
    HealthCard: "HealthCard",
    ReceiveDate: "ReceiveDate",
    PatientPhone: "PatientPhone",
    Action: "Action"
} as const;

const saveOptionKey = "PharmacyOrderTableSetting";
const emptyValue = "--";

function usePharmacyOrdersTable(
    tableSetting: PharmacyOrdersTableSettingsModel,
    [isAllCheckboxChecked, setIsAllCheckboxChecked]: [boolean, React.Dispatch<React.SetStateAction<boolean>>],
    toggleIsChecked: (orderId: Guid, isChecked: boolean) => void,
    sort: (newOrderType: PharmacyOrdersSortOrderType, newOrderDirection: SortDirection) => void,
    toggleArchiveRestore: (orderId: Guid, shouldArchive: boolean) => void,
    completeOrder: (orderId: Guid, orderStatus: PharmacyOrderStatus) => void,
    sendManualReminder: (orderId: Guid) => void,
    filePharmacyOrder: (orderId: Guid) => void
): IUsePharmacyOrdersTableReturn {
    const { getText } = useTranslation();

    const [orderType, setOrderType] = useState(tableSetting.queryOrderType);
    const [orderDirection, setOrderDirection] = useState(tableSetting.queryOrderDirection);

    function getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<TableDataSourceItem<PharmacyOrderModel>>(
            columnsOptions,
            column => ({
                renderCell: item => <div>{formatCellText(column.FieldName, item)}</div>,
                ...(column.FieldName === Columns.Checkbox && {
                    renderHeader: () => renderHeader()
                })
            }),
            false
        );
    }

    function renderHeader(): JSX.Element {
        return (
            <CheckboxControl
                onChange={isChecked => setIsAllCheckboxChecked(isChecked)}
                checked={isAllCheckboxChecked}
                DivWrap={true}
            />
        );
    }

    function formatCheckbox(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element {
        return (
            <CheckboxControl
                id={item.Model.orderId}
                onChange={isChecked => toggleIsChecked(item.Model.orderId, isChecked)}
                checked={item.IsSelected}
            />
        );
    }

    function formatClinic(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element | string {
        return item.Model.clinicName ? <span title={item.Model.clinicName}>{item.Model.clinicName}</span> : emptyValue;
    }

    function formatPrescriber(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element | string {
        return item.Model.prescriberName ? <span title={item.Model.prescriberName}>{item.Model.prescriberName}</span> : emptyValue;
    }

    function formatPrescriptionId(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element | string {
        return item.Model.prescriptionBarcode ? <span title={item.Model.prescriptionBarcode}>{item.Model.prescriptionBarcode}</span> : emptyValue;
    }

    function formatOrderId(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element | string {
        return item.Model.orderId ? <span title={item.Model.orderId}>{item.Model.orderId}</span> : emptyValue;
    }

    function formatLastUpdated(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element | string {
        const lastUpdatedDate = LuxonDateHelper.formatToFullDateTime(item.Model.lastUpdatedDate);
        return item.Model.lastUpdatedDate ? <span title={lastUpdatedDate}>{lastUpdatedDate}</span> : emptyValue;
    }

    function formatPreferredDate(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element | string {
        const dateOnly = LuxonDateTimeParser.parse(item.Model.preferredDate)?.toFormat("yyyy-LL-dd");
        const { iconType, label } = PharmacyOrderHelper.getPreferredTimeOfDay(item.Model.preferredTimeOfDay);

        return iconType ? (
            <span className="inline-flex-center">
                {dateOnly} -
                <span className="margin-xs-left margin-xs-right inline-flex-center">
                    <Icon size="sm">
                        {iconType}
                    </Icon>
                </span>
                {label}
            </span>
        ) : emptyValue;
    }

    function formatPrescription(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element | string {
        const { iconType, label } = PharmacyOrderHelper.getPrescriptionType(item.Model.prescriptionType);
        if (iconType) {
            return (
                <span className="inline-flex-center">
                    <Icon colorVariant="brand" size="sm">
                        {iconType}
                    </Icon>
                    <span className="push-left-8">{label}</span>
                </span>
            );
        }
        else
            return emptyValue;
    }

    function formatPatient(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element {
        return (
            <HyperLinkRedirectControl
                target={RedirectTo.Patient}
                redirectValue={GuidHelper.AsGuid(item.Model.patientContactId)}
                text={NameHelper.formatFullName(item.Model.patientFirstName, item.Model.patientLastName, item.Model.patientPreferredName)}
            />
        );
    }

    function formatOrderStatus(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element | string {
        return (
            <>
                <PharmacyOrdersStatusView
                    orderStatus={item.Model.orderStatus}
                    hasFailedAuthentication={item.Model.hasFailedAuthentication}
                    isAllergiesUpdated={item.Model.isAllergiesUpdated}
                />
                {item.Model.patientReminderSentDate && item.Model.orderStatus === PharmacyOrderStatus.Contacted && (
                    <span className="margin-xs-left display-inline-flex" title={getText("PharmacyOrders_ReminderSentLabel")}>
                        <Icon size="sm" colorVariant="neutral">
                            <ArrowClockwise />
                        </Icon>
                    </span>
                )}
                {item.Model.filedDate && (
                    <span
                        className={`margin-xs-left inline-flex-center ${styles.filedStatus}`}
                        title={`${getText("PharmacyOrders_FiledLabel")} ${LuxonDateHelper.formatToFullDateTime(item.Model.filedDate)}`}
                    >
                        <IconBadge size="sm" variant="uploadSuccess" />
                    </span>
                )}
            </>
        );
    }

    function formatDistribution(item: TableDataSourceItem<PharmacyOrderModel>): string {
        return item.Model.distribution != null ? EnumHelper.getVocab(PharmacyOrderDistributionType, "Enums_PharmacyOrderDistribution_", item.Model.distribution) : emptyValue;
    }

    function formatHealthCard(item: TableDataSourceItem<PharmacyOrderModel>): string {
        return item.Model.patientHealthCard || emptyValue;
    }

    function formatReceiveDate(item: TableDataSourceItem<PharmacyOrderModel>): string {
        return item.Model.receivedDate ? LuxonDateHelper.formatToFullDateTime(item.Model.receivedDate) : emptyValue;
    }

    function formatPatientPhone(item: TableDataSourceItem<PharmacyOrderModel>): string {
        return item.Model.patientPhoneNumbers.length ? PhoneNumber.getLabelFormat(item.Model.patientPhoneNumbers[0].Model.Number) : emptyValue;
    }

    function formatAction(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element {
        const { orderId, orderStatus, isArchived } = item.Model;

        if (isArchived && orderStatus !== PharmacyOrderStatus.Contacted) {
            return (
                <button
                    type="button"
                    className="btn btn-link margin-xs-right padding-none"
                    onClick={() => toggleArchiveRestore(orderId, false)}
                >
                    {getText("RestoreLabel")}
                </button>
            );
        }

        switch (orderStatus) {
            case PharmacyOrderStatus.Completed:
            case PharmacyOrderStatus.Canceled:
                return (
                    <button
                        type="button"
                        className="btn btn-link margin-xs-right padding-none"
                        onClick={() => toggleArchiveRestore(orderId, true)}
                    >
                        {getText("PharmacyOrders_ActionArchive")}
                    </button>
                );
            case PharmacyOrderStatus.NotContacted:
            case PharmacyOrderStatus.OnHold:
            case PharmacyOrderStatus.ToPrepare:
                return renderCompleteLinkButton(item);
            case PharmacyOrderStatus.Contacted:
                return renderContactedOrderAction(item);
            default:
                return null;
        }
    }

    function renderCompleteLinkButton(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element {
        const { orderId, orderStatus } = item.Model;

        return (
            <button
                type="button"
                className="btn btn-link margin-xs-right padding-none"
                onClick={() => completeOrder(orderId, orderStatus)}
            >
                {getText("PharmacyOrders_ActionComplete")}
            </button>
        );
    }

    function renderContactedOrderAction(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element {
        const reminderButton = renderReminderLinkButton(item);
        const fileButton = renderFileLinkButton(item);

        return (
            <>
                {reminderButton}
                {reminderButton && fileButton && (<span className="margin-xs-right">/</span>)}
                {fileButton}
            </>
        );
    }

    function renderReminderLinkButton(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element {
        const { orderId, patientReminderSentDate, receivedDate, orderStatus, hasFailedAuthentication } = item.Model;

        const currentTime = DateTime.now();
        const receivedDateTime = DateTime.fromJSDate(receivedDate);
        const remindLinkVisibleAfter = receivedDateTime.plus({ minutes: 10 });
        const remindLinkVisibleUntil = receivedDateTime.plus({ minutes: cacheStore.ClinicSettings.PrescriptionReminderDelayInMinutes });

        const canSendReminder = patientReminderSentDate == null
            && orderStatus === PharmacyOrderStatus.Contacted
            && hasFailedAuthentication !== true
            && currentTime >= remindLinkVisibleAfter
            && currentTime < remindLinkVisibleUntil;

        if (!canSendReminder) {
            return null;
        }

        return (
            <button
                type="button"
                className="btn btn-link margin-xs-right padding-none"
                onClick={() => sendManualReminder(orderId)}
            >
                {getText("ReminderDLabel")}
            </button>
        );
    }

    function renderFileLinkButton(item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element {
        const { orderId, orderStatus, filedDate } = item.Model;

        const showFileButton = orderStatus === PharmacyOrderStatus.Contacted && !filedDate;

        if (!showFileButton) {
            return null;
        }

        return (
            <button
                type="button"
                className="btn btn-link padding-none"
                onClick={() => filePharmacyOrder(orderId)}
            >
                {getText("PharmacyOrders_FileLabel")}
            </button>
        );
    }

    function formatCellText(fieldName: string, item: TableDataSourceItem<PharmacyOrderModel>): JSX.Element | string {
        const formatFunctions: { [key: string]: (item: TableDataSourceItem<PharmacyOrderModel>) => JSX.Element | string } = {
            [Columns.Checkbox]: formatCheckbox,
            [Columns.Clinic]: formatClinic,
            [Columns.Prescriber]: formatPrescriber,
            [Columns.PrescriptionId]: formatPrescriptionId,
            [Columns.OrderId]: formatOrderId,
            [Columns.LastUpdated]: formatLastUpdated,
            [Columns.PreferredDate]: formatPreferredDate,
            [Columns.Distribution]: formatDistribution,
            [Columns.Prescription]: formatPrescription,
            [Columns.OrderStatus]: formatOrderStatus,
            [Columns.Patient]: formatPatient,
            [Columns.HealthCard]: formatHealthCard,
            [Columns.ReceiveDate]: formatReceiveDate,
            [Columns.PatientPhone]: formatPatientPhone,
            [Columns.Action]: formatAction
        };

        return formatFunctions[fieldName] ? formatFunctions[fieldName](item) : null;
    }

    function getTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        const defaultTablePluginOptions = {
            MinColWidth: 50,
            MinTableWidth: 950,
            Cols: columnOptions.filter(p => !p.IsHidden).map(p => p.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    function getCommonTableColumnOptions(options: {
        headerText: string,
        fieldName: string,
        widthPercentage: number,
        isSortable: boolean,
        sortColumn?: PharmacyOrdersSortOrderType,
        isRowClickable?: boolean,
        isFixedInPlace?: boolean,
        isHidden?: boolean
    }): MyleTableColumnOptions {
        const sortTable = (newOrderType: PharmacyOrdersSortOrderType): void => {
            const isOrderTypeDifferent = orderType !== newOrderType;
            if (isOrderTypeDifferent) {
                setOrderType(newOrderType);
            }

            const newOrderDirection = MyleTableHelper.getNewSortDir(newOrderType, orderType, orderDirection);
            const isOrderDirectionDifferent = orderDirection !== newOrderDirection;
            if (isOrderDirectionDifferent) {
                setOrderDirection(newOrderDirection);
            }

            if (isOrderTypeDifferent || isOrderDirectionDifferent) {
                sort(newOrderType, newOrderDirection);
            }
        };

        return new MyleTableColumnOptions({
            HeaderText: options.headerText,
            FieldName: options.fieldName,
            RowClickEnabled: options.isRowClickable ?? true,
            IsSortable: options.isSortable ?? true,
            SortColumn: options.sortColumn,
            SortDirection: orderDirection,
            IsSortActive: orderType === options.sortColumn,
            WidthPercentageDefault: options.widthPercentage,
            IsFixedInPlace: options.isFixedInPlace ?? false,
            IsHidden: options.isHidden ?? false,
            sortingMethod: () => sortTable(options.sortColumn)
        });
    }

    function getMyleTableColumnOptions(hasUserSetting: boolean): MyleTableColumnOptions[] {
        const defaultColumnsWidthInPercentage = hasUserSetting ? 7 : 9;
        const widthInPercentage = hasUserSetting ? 7 : 3;

        return [
            getCommonTableColumnOptions({
                headerText: "",
                fieldName: Columns.Checkbox,
                widthPercentage: 3,
                isSortable: false,
                isRowClickable: false,
                isFixedInPlace: true
            }),
            getCommonTableColumnOptions({
                headerText: getText("PharmacyOrders_PreferredDate"),
                fieldName: Columns.PreferredDate,
                sortColumn: PharmacyOrdersSortOrderType.PreferredDate,
                widthPercentage: defaultColumnsWidthInPercentage,
                isSortable: true
            }),
            getCommonTableColumnOptions({
                headerText: getText("PharmacyOrders_DistributionLabel"),
                fieldName: Columns.Distribution,
                widthPercentage: defaultColumnsWidthInPercentage,
                isSortable: false
            }),
            getCommonTableColumnOptions({
                headerText: getText("PharmacyOrders_PrescriptionLabel"),
                fieldName: Columns.Prescription,
                widthPercentage: defaultColumnsWidthInPercentage,
                isSortable: false
            }),
            getCommonTableColumnOptions({
                headerText: getText("PharmacyOrders_OrderStatusLabel"),
                fieldName: Columns.OrderStatus,
                widthPercentage: defaultColumnsWidthInPercentage,
                isSortable: false
            }),
            getCommonTableColumnOptions({
                headerText: getText("PatientLabel"),
                fieldName: Columns.Patient,
                widthPercentage: defaultColumnsWidthInPercentage,
                isSortable: false
            }),
            getCommonTableColumnOptions({
                headerText: getText("HealthCard"),
                fieldName: Columns.HealthCard,
                widthPercentage: defaultColumnsWidthInPercentage,
                isSortable: false
            }),
            getCommonTableColumnOptions({
                headerText: getText("ReceivedDateLabel"),
                fieldName: Columns.ReceiveDate,
                sortColumn: PharmacyOrdersSortOrderType.ReceivedDate,
                widthPercentage: defaultColumnsWidthInPercentage,
                isSortable: true
            }),
            getCommonTableColumnOptions({
                headerText: getText("PharmacyOrders_PatientPhoneLabel"),
                fieldName: Columns.PatientPhone,
                widthPercentage: defaultColumnsWidthInPercentage,
                isSortable: false
            }),
            getCommonTableColumnOptions({
                headerText: getText("PharmacyOrders_LastUpdatedLabel"),
                fieldName: Columns.LastUpdated,
                sortColumn: PharmacyOrdersSortOrderType.LastUpdated,
                widthPercentage: widthInPercentage,
                isSortable: true,
                isHidden: !hasUserSetting
            }),
            getCommonTableColumnOptions({
                headerText: getText("CliniqueLabel"),
                fieldName: Columns.Clinic,
                widthPercentage: widthInPercentage,
                isSortable: false,
                isHidden: !hasUserSetting
            }),
            getCommonTableColumnOptions({
                headerText: getText("PharmacyOrders_PrescriberLabel"),
                fieldName: Columns.Prescriber,
                widthPercentage: widthInPercentage,
                isSortable: false,
                isHidden: !hasUserSetting
            }),
            getCommonTableColumnOptions({
                headerText: getText("PharmacyOrders_PrescriptionIdLabel"),
                fieldName: Columns.PrescriptionId,
                widthPercentage: widthInPercentage,
                isSortable: false,
                isHidden: !hasUserSetting
            }),
            getCommonTableColumnOptions({
                headerText: getText("PharmacyOrders_OrderIdLabel"),
                fieldName: Columns.OrderId,
                widthPercentage: widthInPercentage,
                isSortable: false,
                isHidden: !hasUserSetting
            }),
            getCommonTableColumnOptions({
                headerText: getText("ActionLabel"),
                fieldName: Columns.Action,
                widthPercentage: defaultColumnsWidthInPercentage,
                isSortable: false,
                isRowClickable: false,
                isFixedInPlace: true
            })
        ];
    }

    const hasPharmacyOrdersUserSettings = !!cacheStore.UserSettings?.GridsDisplaySettings?.any(p => p.Key === saveOptionKey);
    const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(saveOptionKey, getMyleTableColumnOptions(hasPharmacyOrdersUserSettings));
    const columns = getTableColumns(columnsOptions);
    const tableRenderOptions = getTableRenderOptions(columnsOptions);
    const columnConfigurationModels = new MyleHelper().castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

    return {
        columns,
        tableRenderOptions,
        columnConfigurationModels,
        saveOptionKey
    };
}

export { usePharmacyOrdersTable };
